import {
  useGetStudentImportLazyQuery,
  useGetStudentImportsLazyQuery,
} from '@/types/vue-apollo-helpers';
import type { GetStudentImportQuery, GetStudentImportsQuery } from '@/types/operations';

export type StudentImportRecord = NonNullable<GetStudentImportQuery['studentImport']>;
export type StudentImportListItem = GetStudentImportsQuery['studentImports']['data'][number];

const TERMINAL_STATUSES = ['completed', 'completed_with_errors', 'failed'] as const;
const DEFAULT_POLL_MS = 3000;

export function isTerminal(status: string | null | undefined): boolean {
  return !!status && (TERMINAL_STATUSES as readonly string[]).includes(status);
}

/**
 * Watch a single StudentImport row, polling until it reaches a terminal state.
 * Returns reactive `record`, `loading`, `error`, and a `start(id)` / `stop()` API.
 */
export function useStudentImportPolling(opts: { intervalMs?: number } = {}) {
  const intervalMs = opts.intervalMs ?? DEFAULT_POLL_MS;

  const importId = ref<string | null>(null);
  const record = ref<StudentImportRecord | null>(null);

  const { result, loading, error, load, refetch, stop: stopQuery } = useGetStudentImportLazyQuery(
    { id: '' },
    { fetchPolicy: 'network-only' },
  );

  let pollHandle: ReturnType<typeof setInterval> | null = null;

  function clearPoll() {
    if (pollHandle) {
      clearInterval(pollHandle);
      pollHandle = null;
    }
  }

  function stop() {
    clearPoll();
    stopQuery();
    importId.value = null;
    record.value = null;
  }

  function start(id: string) {
    if (!id) return;
    if (importId.value === id) return;
    clearPoll();
    importId.value = id;
    load(undefined, { id });
    pollHandle = setInterval(() => {
      if (!importId.value) return;
      refetch({ id: importId.value });
    }, intervalMs);
  }

  watch(result, (newVal) => {
    const fresh = newVal?.studentImport ?? null;
    if (!fresh) return;
    record.value = fresh;
    if (isTerminal(fresh.status)) {
      clearPoll();
    }
  });

  const isPolling = computed(() => pollHandle !== null);
  const isDone = computed(() => isTerminal(record.value?.status));

  return {
    importId: computed(() => importId.value),
    record: computed(() => record.value),
    loading,
    error,
    isPolling,
    isDone,
    start,
    stop,
    refetch: () => importId.value && refetch({ id: importId.value }),
  };
}

/**
 * Lists recent imports for the current school. Auto-polls while any in-flight
 * imports remain (status is pending or processing).
 */
export function useStudentImportsList(opts: { first?: number; intervalMs?: number } = {}) {
  const first = opts.first ?? 20;
  const intervalMs = opts.intervalMs ?? DEFAULT_POLL_MS * 2;

  const { result, loading, error, load, refetch } = useGetStudentImportsLazyQuery(
    { first, page: 1 },
    { fetchPolicy: 'network-only' },
  );

  const items = computed<StudentImportListItem[]>(() => result.value?.studentImports?.data ?? []);
  const inFlightCount = computed(() => items.value.filter((i) => !isTerminal(i.status)).length);

  let pollHandle: ReturnType<typeof setInterval> | null = null;

  function startPollingIfNeeded() {
    if (pollHandle || inFlightCount.value === 0) return;
    pollHandle = setInterval(() => refetch(), intervalMs);
  }

  function stopPolling() {
    if (pollHandle) {
      clearInterval(pollHandle);
      pollHandle = null;
    }
  }

  watch(inFlightCount, (count) => {
    if (count > 0) startPollingIfNeeded();
    else stopPolling();
  });

  function fetch() {
    load(undefined, { first, page: 1 });
  }

  return {
    items,
    inFlightCount,
    loading,
    error,
    fetch,
    refetch: () => refetch(),
    stopPolling,
  };
}
